import { useRef, useState } from 'react';
import { Calendar, Check, Flag, MessageCircle, Clock } from 'lucide-react';
import type { Task } from '@unitly/contracts';
import { addDays, formatDue } from '../../lib/dates.ts';
import { AvatarStack, wsColor } from '../../ui/primitives.tsx';
import { useOrgCtx } from '../../app/org-context.tsx';
import { useToggleTask, useUpdateTask } from '../../api/queries.ts';
import { useToast } from '../../ui/Toast.tsx';

const SWIPE = 84;

export function TaskCheck({ task, onToggle }: { task: Task; onToggle: (done: boolean) => void }) {
  const done = task.state.category === 'done';
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={done}
      aria-label={done ? `Riapri ${task.title}` : `Completa ${task.title}`}
      className="check"
      data-priority={task.priority}
      disabled={!task.permissions.edit}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(!done);
      }}
    >
      <Check size={14} strokeWidth={3} />
    </button>
  );
}

export function DueLabel({ task }: { task: Task }) {
  const { today } = useOrgCtx();
  if (!task.dueDate) return null;
  const cls = task.state.category === 'done' ? '' : task.isOverdue ? ' overdue' : task.dueDate === today ? ' today' : '';
  return (
    <span className={`meta-item${cls}`}>
      {task.dueTime ? <Clock size={13} /> : <Calendar size={13} />}
      {formatDue(task.dueDate, task.dueTime, today)}
    </span>
  );
}

export function TaskMeta({ task, showWorkspace = true }: { task: Task; showWorkspace?: boolean }) {
  return (
    <span className="task-meta">
      <DueLabel task={task} />
      {task.priority > 0 && task.priority <= 3 && (
        <span className="meta-item">
          <Flag size={13} className="prio-flag" data-p={task.priority} fill="currentColor" />
        </span>
      )}
      {showWorkspace && (
        <span className="meta-item">
          <span className="ws-dot" style={{ '--ws-color': wsColor(task.workspaceColor) } as React.CSSProperties} />
          {task.workspaceName}
        </span>
      )}
      {task.commentCount > 0 && (
        <span className="meta-item">
          <MessageCircle size={13} /> {task.commentCount}
        </span>
      )}
      <AvatarStack people={task.assignees} />
    </span>
  );
}

/** A task row. Tap opens it; swipe right completes, swipe left moves it to tomorrow (touch only). */
export function TaskRow({ task, showWorkspace = true }: { task: Task; showWorkspace?: boolean }) {
  const { orgId, openTask, today } = useOrgCtx();
  const toggle = useToggleTask(orgId);
  const update = useUpdateTask(orgId);
  const toast = useToast();
  const rowRef = useRef<HTMLDivElement>(null);
  const start = useRef<{ x: number; y: number; locked?: 'x' | 'y' } | null>(null);
  const [dx, setDx] = useState(0);
  const done = task.state.category === 'done';

  const setDone = (value: boolean) => {
    toggle.mutate({ id: task.id, done: value });
    if (value) toast('Task completata', { label: 'Annulla', onClick: () => toggle.mutate({ id: task.id, done: false }) });
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch' || !task.permissions.edit) return;
    start.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const s = start.current;
    if (!s) return;
    const mx = e.clientX - s.x;
    const my = e.clientY - s.y;
    if (!s.locked) {
      if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
      s.locked = Math.abs(mx) > Math.abs(my) ? 'x' : 'y';
    }
    if (s.locked === 'x') setDx(Math.max(-140, Math.min(140, mx)));
  };
  const onPointerUp = () => {
    const s = start.current;
    start.current = null;
    if (!s || s.locked !== 'x') return setDx(0);
    if (dx > SWIPE) {
      setDone(!done);
    } else if (dx < -SWIPE && !done) {
      const prev = { dueDate: task.dueDate, dueTime: task.dueTime };
      update.mutate({ id: task.id, patch: { dueDate: addDays(today, 1) } });
      toast('Spostata a domani', { label: 'Annulla', onClick: () => update.mutate({ id: task.id, patch: prev }) });
    }
    setDx(0);
  };

  return (
    <div className="task-row-wrap">
      {dx !== 0 && (
        <div className={`swipe-bg ${dx > 0 ? 'right' : 'left'}`} aria-hidden="true">
          {dx > 0 ? <span>{done ? 'Riapri' : 'Completa'}</span> : <span>Domani</span>}
        </div>
      )}
      <div
        ref={rowRef}
        className={`task-row${done ? ' done' : ''}${dx !== 0 ? ' dragging' : ''}`}
        style={dx ? { transform: `translateX(${dx}px)` } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <TaskCheck task={task} onToggle={setDone} />
        <button type="button" className="task-main" onClick={() => dx === 0 && openTask(task.id)}>
          <span className="task-title">{task.title}</span>
          <TaskMeta task={task} showWorkspace={showWorkspace} />
        </button>
      </div>
    </div>
  );
}
